import { useState, forwardRef } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Calendar, X } from "lucide-react";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { formatDate } from "@/lib/format";

export interface RangePickerValue {
  from?: Date;
  to?: Date;
}

export interface RangePresetItem {
  label: string;
  /** Bugun'dan necha kun oldin (0 = bugun) */
  days: number;
}

export interface RangePickerProps {
  value: RangePickerValue | null | undefined;
  onChange: (value: RangePickerValue | null) => void;
  presets?: RangePresetItem[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
}

const DEFAULT_PRESETS: RangePresetItem[] = [
  { label: "Bugun", days: 0 },
  { label: "7 kun", days: 7 },
  { label: "30 kun", days: 30 },
  { label: "90 kun", days: 90 },
];

function presetRange(days: number): RangePickerValue {
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  if (days > 0) from.setDate(from.getDate() - days);
  return { from, to };
}

export const RangePicker = forwardRef<HTMLButtonElement, RangePickerProps>(function RangePicker(
  {
    value,
    onChange,
    presets = DEFAULT_PRESETS,
    placeholder = "Sana oraliq",
    disabled,
    className,
    clearable = true,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const display =
    value?.from && value?.to
      ? `${formatDate(value.from)} — ${formatDate(value.to)}`
      : value?.from
      ? `${formatDate(value.from)} — …`
      : placeholder;

  const dpRange: DateRange | undefined = value?.from
    ? { from: value.from, to: value.to }
    : undefined;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={ref}
          type="button"
          disabled={disabled}
          aria-label="Sana oralig'ini tanlash"
          className={cn(
            "inline-flex h-10 items-center gap-2 rounded-lg border bg-zinc-950/60 px-3 text-sm",
            "border-zinc-800 text-zinc-100 transition-colors duration-150",
            "hover:border-zinc-700 focus:border-[color:var(--brand-primary)]/60 focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)]/30",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <Calendar className="h-4 w-4 text-[var(--text-muted)]" aria-hidden />
          <span className={cn(!value?.from && "text-zinc-500")}>{display}</span>
          {clearable && value?.from && !disabled && (
            <span
              role="button"
              aria-label="Tozalash"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-[var(--bg-surface)]"
            >
              <X className="h-3 w-3 text-[var(--text-muted)]" />
            </span>
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-[var(--z-popover,60)] flex rounded-lg border border-[var(--border-strong)] bg-[var(--bg-elevated)]",
            "shadow-[var(--shadow-modal)] overflow-hidden",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          )}
        >
          <div className="flex w-[140px] flex-col border-r border-[var(--border-subtle)] p-2">
            <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              Tezkor
            </div>
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  onChange(presetRange(preset.days));
                  setOpen(false);
                }}
                className="rounded-md px-2 py-1.5 text-left text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col">
            <div className="p-2">
              <DayPicker
                mode="range"
                numberOfMonths={2}
                selected={dpRange}
                onSelect={(range) => {
                  onChange(range ? { from: range.from, to: range.to } : null);
                }}
                className="rdp-prism"
                showOutsideDays
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] px-3 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                Tozalash
              </Button>
              <Button variant="primary" size="sm" onClick={() => setOpen(false)}>
                Tasdiqlash
              </Button>
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});
